import React, { useState, useEffect } from 'react';
import ModalLayer from './ModalLayer';
import { supabase } from '../supabaseClient';
import { useTheme } from '../ThemeContext';
import { getYouTubeThumbnail, isValidYouTubeUrl } from '../utils/youtubeHelpers';
import { libraryCategoryGroups } from '../shared/taskLibraryGroups';
import { canWriteGlobalTasks } from '../shared/globalTaskForm';
import { showToast } from './Toast';
import { openExternalUrl } from '../utils/openExternal';
import { compressTaskPhoto } from '../utils/taskPhotoFile';
import { useSignedStorageUrl } from '../shared/signStorageObject';

const TaskLibrary = ({ profile }) => {
  const { colors } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openCategories, setOpenCategories] = useState({});
  const [editingTask, setEditingTask] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    image_url: '',
    diy_tips: '',
    youtube_video_url: '',
    difficulty_level: 'Easy',
    estimated_time: '',
    when_to_call_pro: ''
  });
  const previewUrl = useSignedStorageUrl('task-photos', formData.image_url);

  useEffect(() => {
    loadTasks();
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTasks = async () => {
    if (!profile?.id) return;

    try {
      // Load global preset tasks (where agent_id is null)
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .is('agent_id', null)
        .order('category', { ascending: true });

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  const canEdit = canWriteGlobalTasks(profile);

  const handleToggleActive = async (task) => {
    if (!canEdit) return;
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ active: !task.active })
        .eq('id', task.id);

      if (error) throw error;

      setEditingTask((current) => (
        current && current.id === task.id ? { ...current, active: !task.active } : current
      ));
      await loadTasks();
    } catch (error) {
      console.error('Error toggling task:', error);
      showToast('Error updating task. Please try again.');
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setFormData({
      image_url: task.image_url || '',
      diy_tips: task.diy_tips || '',
      youtube_video_url: task.youtube_video_url || '',
      difficulty_level: task.difficulty_level || 'Easy',
      estimated_time: task.estimated_time || '',
      when_to_call_pro: task.when_to_call_pro || ''
    });
  };

  const handleFileUpload = async (event) => {
    if (!canEdit) return;
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'];
    if (!validTypes.includes(file.type)) {
      showToast('Please upload a valid image file (JPEG, PNG, or HEIC)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('File size must be less than 5MB');
      return;
    }

    setUploading(true);

    try {
      if (!editingTask || !editingTask.id) {
        showToast('Open a task before uploading a photo.');
        return;
      }
      let body = file;
      let fileExt = file.name.split('.').pop();
      try {
        const prepared = await compressTaskPhoto(file);
        if (prepared && prepared.size > 0 && prepared.size < file.size) {
          body = prepared;
          fileExt = 'jpg';
        }
      } catch (prepareError) {
        body = file;
      }
      const filePath = `${editingTask.id}/${Date.now()}.${fileExt}`;

      const { error } = await supabase.storage
        .from('task-photos')
        .upload(filePath, body, {
          cacheControl: '3600',
          upsert: false,
          contentType: body.type || file.type || 'image/jpeg',
        });

      if (error) throw error;

      // The bucket is private. This address is the stored locator.
      // The library and the homeowner app read it with a signed URL.
      const { data: { publicUrl } } = supabase.storage
        .from('task-photos')
        .getPublicUrl(filePath);

      // Update form data with the new URL
      setFormData(prev => ({
        ...prev,
        image_url: publicUrl
      }));

      showToast('Photo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading file:', error);
      showToast('Error uploading photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateTask = async () => {
    if (!canEdit || !editingTask) return;

    // Validate YouTube URL if provided
    if (formData.youtube_video_url && !isValidYouTubeUrl(formData.youtube_video_url)) {
      showToast('Please enter a valid YouTube URL (e.g., https://www.youtube.com/watch?v=...)');
      return;
    }

    try {
      const { error } = await supabase
        .from('tasks')
        .update({
          image_url: formData.image_url,
          diy_tips: formData.diy_tips,
          youtube_video_url: formData.youtube_video_url,
          difficulty_level: formData.difficulty_level,
          estimated_time: formData.estimated_time,
          when_to_call_pro: formData.when_to_call_pro
        })
        .eq('id', editingTask.id);

      if (error) throw error;

      await loadTasks();
      setEditingTask(null);
      setFormData({
        image_url: '',
        diy_tips: '',
        youtube_video_url: '',
        difficulty_level: 'Easy',
        estimated_time: '',
        when_to_call_pro: ''
      });
      showToast('Task updated successfully!');
    } catch (error) {
      console.error('Error updating task:', error);
      showToast('Error updating task. Please try again.');
    }
  };

  const filteredTasks = activeFilter === 'all'
    ? tasks
    : tasks.filter(t => t.type === activeFilter);
  const categoryGroups = libraryCategoryGroups(filteredTasks, searchQuery);

  return (
    <div>
      <h2 style={{...styles.pageTitle, color: colors.text, marginBottom: '1rem'}}>Task Library</h2>

      <p style={{...styles.description, color: colors.textSecondary, marginBottom: '1.5rem'}}>
        These tasks are shared with your homeowners.
        {canEdit ? '' : ' Pacarion updates them.'}
      </p>

      <div style={{...styles.tabs, borderBottom: `2px solid ${colors.border}`, marginBottom: '1.5rem'}}>
        <div
          style={{
            ...styles.tab,
            color: activeFilter === 'all' ? colors.primary : colors.textSecondary,
            borderBottomColor: activeFilter === 'all' ? colors.primary : 'transparent'
          }}
          onClick={() => setActiveFilter('all')}
        >
          All Tasks
        </div>
        <div
          style={{
            ...styles.tab,
            color: activeFilter === 'maintenance' ? colors.primary : colors.textSecondary,
            borderBottomColor: activeFilter === 'maintenance' ? colors.primary : 'transparent'
          }}
          onClick={() => setActiveFilter('maintenance')}
        >
          Maintenance
        </div>
        <div
          style={{
            ...styles.tab,
            color: activeFilter === 'financial' ? colors.primary : colors.textSecondary,
            borderBottomColor: activeFilter === 'financial' ? colors.primary : 'transparent'
          }}
          onClick={() => setActiveFilter('financial')}
        >
          Financial
        </div>
      </div>

      <input
        type="search"
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        placeholder="Search tasks"
        aria-label="Search tasks"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          marginBottom: '1rem',
          padding: '0.85rem 1rem',
          borderRadius: '0.75rem',
          border: `1px solid ${colors.border}`,
          background: colors.surface,
          color: colors.text,
          fontSize: '1rem',
          minHeight: '48px'
        }}
      />

      <div style={styles.taskList}>
        {categoryGroups.length === 0 ? (
          <p style={{...styles.emptyState, color: colors.textSecondary}}>
            No tasks found.
          </p>
        ) : (
          categoryGroups.map((group) => {
            const open = Boolean(searchQuery.trim()) || openCategories[group.name];
            return (
              <div key={group.name} style={{ marginBottom: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setOpenCategories((current) => ({
                    ...current,
                    [group.name]: !current[group.name]
                  }))}
                  style={{
                    width: '100%',
                    minHeight: '56px',
                    padding: '0.9rem 1rem',
                    borderRadius: '1rem',
                    border: `1px solid ${colors.border}`,
                    background: colors.surface,
                    color: colors.text,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '1rem',
                    fontWeight: 650,
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <span>{group.name}</span>
                  <span style={{ color: colors.textSecondary, fontWeight: 500 }}>{group.tasks.length}</span>
                </button>
                {open && (
                  <div style={{ marginTop: '0.35rem' }}>
                    {group.tasks.map((task) => (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() => handleEditTask(task)}
                        style={{
                          width: '100%',
                          minHeight: '52px',
                          padding: '0.75rem 1rem',
                          border: 'none',
                          borderBottom: `1px solid ${colors.border}`,
                          background: 'transparent',
                          color: colors.text,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '1rem',
                          cursor: 'pointer',
                          textAlign: 'left',
                          opacity: task.active ? 1 : 0.55
                        }}
                      >
                        <span>{task.title}</span>
                        <span style={{ color: colors.textSecondary }} aria-hidden="true">›</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Edit DIY Info Modal */}
      {editingTask && (
        <ModalLayer style={styles.modalOverlay} onClick={() => setEditingTask(null)}>
          <div className="app-modal-card" style={{...styles.modal, background: colors.surface, border: `1px solid ${colors.border}`}} onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header" style={{ background: colors.surface }}>
              <h3 style={{margin: 0, color: colors.text}}>
                Edit: {editingTask.title}
              </h3>
              <button type="button" className="app-modal-close" aria-label="Close" onClick={() => setEditingTask(null)} style={{ color: colors.text }}>
                ✕
              </button>
            </div>
            {canEdit ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', color: colors.text }}>
                <input
                  type="checkbox"
                  checked={Boolean(editingTask.active)}
                  onChange={() => handleToggleActive(editingTask)}
                />
                Active for homeowners
              </label>
            ) : (
              <p style={{ margin: '0 0 1rem', color: colors.textSecondary }}>
                {editingTask.active === false ? 'Inactive. ' : ''}Pacarion updates these tasks.
              </p>
            )}

            {/* Task Image Section */}
            <div style={{marginBottom: '2rem', padding: '1rem', background: colors.surfaceAlt, borderRadius: '8px'}}>
              <h4 style={{margin: '0 0 1rem', color: colors.text, fontSize: '1rem'}}>Task Image</h4>

              {/* File Upload Option */}
              {canEdit ? (
              <div style={{marginBottom: '1rem'}}>
                <label style={{...styles.label, color: colors.text}}>Upload Photo</label>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/heic,image/heif"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  style={{
                    ...styles.input,
                    background: colors.background,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    padding: '0.5rem'
                  }}
                />
                {uploading && (
                  <p style={{fontSize: '0.85rem', color: '#59c4c4', marginTop: '0.5rem'}}>
                    Uploading photo...
                  </p>
                )}
              </div>
              ) : null}

              {canEdit ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                margin: '1rem 0',
                gap: '1rem'
              }}>
                <div style={{flex: 1, height: '1px', background: colors.border}}></div>
                <span style={{color: colors.textSecondary, fontSize: '0.85rem'}}>OR</span>
                <div style={{flex: 1, height: '1px', background: colors.border}}></div>
              </div>
              ) : null}

              {/* URL Input Option */}
              <div>
                <label style={{...styles.label, color: colors.text}}>Enter Image URL</label>
                <input
                  type="url"
                  value={formData.image_url}
                  readOnly={!canEdit}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  style={{...styles.input, background: colors.background, color: colors.text, border: `1px solid ${colors.border}`}}
                  placeholder="https://images.unsplash.com/photo-..."
                />
              </div>

              {previewUrl && (
                <div style={{marginTop: '1rem'}}>
                  <label style={{...styles.label, color: colors.text}}>Preview</label>
                  <div style={{textAlign: 'center', padding: '1rem', background: colors.background, borderRadius: '8px'}}>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{maxWidth: '100%', maxHeight: '150px', borderRadius: '8px'}}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* DIY Section */}
            <div style={{marginBottom: '2rem', padding: '1rem', background: colors.surfaceAlt, borderRadius: '8px'}}>
              <h4 style={{margin: '0 0 1rem', color: colors.text, fontSize: '1rem'}}>DIY Information</h4>

              {/* Difficulty Level */}
              <div style={{marginBottom: '1rem'}}>
                <label style={{...styles.label, color: colors.text}}>Difficulty Level</label>
                <select
                  value={formData.difficulty_level}
                  disabled={!canEdit}
                  onChange={(e) => setFormData({...formData, difficulty_level: e.target.value})}
                  style={{...styles.input, background: colors.background, color: colors.text, border: `1px solid ${colors.border}`}}
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>

              {/* Estimated Time */}
              <div style={{marginBottom: '1rem'}}>
                <label style={{...styles.label, color: colors.text}}>Estimated Time</label>
                <input
                  type="text"
                  value={formData.estimated_time}
                  readOnly={!canEdit}
                  onChange={(e) => setFormData({...formData, estimated_time: e.target.value})}
                  style={{...styles.input, background: colors.background, color: colors.text, border: `1px solid ${colors.border}`}}
                  placeholder="e.g., 15 minutes, 1-2 hours"
                />
              </div>

              {/* DIY Tips */}
              <div style={{marginBottom: '1rem'}}>
                <label style={{...styles.label, color: colors.text}}>DIY Tips & Instructions</label>
                <textarea
                  value={formData.diy_tips}
                  readOnly={!canEdit}
                  onChange={(e) => setFormData({...formData, diy_tips: e.target.value})}
                  style={{
                    ...styles.input,
                    background: colors.background,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    minHeight: '120px',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                  placeholder="Step-by-step instructions for homeowners. Include tools needed, materials, and tips for success."
                />
              </div>

              {/* When to Call a Pro */}
              <div>
                <label style={{...styles.label, color: colors.text}}>When to Call a Professional</label>
                <textarea
                  value={formData.when_to_call_pro}
                  readOnly={!canEdit}
                  onChange={(e) => setFormData({...formData, when_to_call_pro: e.target.value})}
                  style={{
                    ...styles.input,
                    background: colors.background,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    minHeight: '80px',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                  placeholder="Red flags that indicate professional help is needed (e.g., mold growth, electrical issues, structural damage)"
                />
              </div>
            </div>

            {/* YouTube Video Section */}
            <div style={{marginBottom: '2rem', padding: '1rem', background: colors.surfaceAlt, borderRadius: '8px'}}>
              <h4 style={{margin: '0 0 1rem', color: colors.text, fontSize: '1rem'}}>Tutorial Video</h4>

              <div style={{marginBottom: '1rem'}}>
                <label style={{...styles.label, color: colors.text}}>YouTube Video URL</label>
                <input
                  type="url"
                  value={formData.youtube_video_url}
                  readOnly={!canEdit}
                  onChange={(e) => setFormData({...formData, youtube_video_url: e.target.value})}
                  style={{...styles.input, background: colors.background, color: colors.text, border: `1px solid ${colors.border}`}}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <p style={{fontSize: '0.85rem', color: colors.textSecondary, margin: '0.5rem 0 0'}}>
                  Paste a YouTube link to show homeowners how to complete this task
                </p>
              </div>

              {formData.youtube_video_url && getYouTubeThumbnail(formData.youtube_video_url) && (
                <div>
                  <label style={{...styles.label, color: colors.text}}>Video Preview</label>
                  <div style={{textAlign: 'center', padding: '1rem', background: colors.background, borderRadius: '8px'}}>
                    <img
                      src={getYouTubeThumbnail(formData.youtube_video_url)}
                      alt="YouTube thumbnail"
                      style={{maxWidth: '100%', borderRadius: '8px', cursor: 'pointer'}}
                      onClick={() => openExternalUrl(formData.youtube_video_url)}
                    />
                    <p style={{fontSize: '0.85rem', color: colors.textSecondary, marginTop: '0.5rem'}}>
                      Click to preview video
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="app-modal-actions" style={{justifyContent: 'flex-end', borderTop: `1px solid ${colors.border}`, background: colors.surface}}>
              <button
                onClick={() => setEditingTask(null)}
                style={{...styles.button, ...styles.buttonSecondary}}
              >
                Cancel
              </button>
              {canEdit ? (
                <button
                  onClick={handleUpdateTask}
                  style={{...styles.button, ...styles.buttonPrimary}}
                >
                  Save Changes
                </button>
              ) : null}
            </div>
          </div>
        </ModalLayer>
      )}
    </div>
  );
};

const styles = {
  pageTitle: {
    marginBottom: '1.5rem',
    fontSize: '1.8rem'
  },
  card: {
    background: 'white',
    padding: '2rem',
    borderRadius: '1rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  },
  description: {
    color: '#666',
    fontSize: window.innerWidth < 768 ? '0.9rem' : '1rem',
    lineHeight: '1.6'
  },
  tabs: {
    display: 'flex',
    gap: window.innerWidth < 768 ? '0.5rem' : '1.5rem',
    borderBottom: '2px solid #eee',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch'
  },
  tab: {
    padding: window.innerWidth < 768 ? '0.75rem 1rem' : '1rem 1.5rem',
    cursor: 'pointer',
    borderBottom: '3px solid transparent',
    fontWeight: 600,
    color: '#666',
    whiteSpace: 'nowrap',
    fontSize: window.innerWidth < 768 ? '0.9rem' : '1rem',
    transition: 'all 0.2s'
  },
  tabActive: {
    color: '#59c4c4',
    borderBottomColor: '#59c4c4'
  },
  taskList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    width: '100%'
  },
  taskCard: {
    border: '1px solid #eee',
    padding: window.innerWidth < 768 ? '1.25rem' : '2rem',
    borderRadius: window.innerWidth < 768 ? '1.25rem' : '0.75rem',
    transition: 'all 0.2s',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    width: '100%',
    boxSizing: 'border-box'
  },
  taskContent: {
    display: 'flex',
    flexDirection: window.innerWidth < 768 ? 'column' : 'row',
    gap: window.innerWidth < 768 ? '1.25rem' : '1.5rem',
    alignItems: window.innerWidth < 768 ? 'stretch' : 'flex-start'
  },
  taskTitle: {
    marginBottom: '0.75rem',
    fontSize: window.innerWidth < 768 ? '1.1rem' : '1.2rem',
    fontWeight: '600',
    lineHeight: '1.3'
  },
  taskDescription: {
    color: '#555',
    fontSize: window.innerWidth < 768 ? '0.9rem' : '0.95rem',
    marginBottom: '1rem',
    marginTop: '0',
    lineHeight: '1.6',
    padding: '0.75rem',
    background: 'rgba(0,0,0,0.02)',
    borderRadius: '0.5rem',
    borderLeft: '3px solid #59c4c4'
  },
  taskMeta: {
    color: '#666',
    fontSize: window.innerWidth < 768 ? '0.85rem' : '0.9rem',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    marginBottom: '0.75rem'
  },
  requiredFeature: {
    fontSize: window.innerWidth < 768 ? '0.8rem' : '0.85rem',
    color: '#666',
    marginTop: '0.5rem',
    padding: '0.5rem 0.75rem',
    background: 'rgba(245, 158, 11, 0.1)',
    borderRadius: '0.5rem',
    display: 'inline-block',
    fontWeight: '600'
  },
  propertyTypes: {
    fontSize: window.innerWidth < 768 ? '0.8rem' : '0.85rem',
    color: '#59c4c4',
    marginTop: '0.5rem',
    fontWeight: '600',
    padding: '0.5rem 0.75rem',
    background: 'rgba(89, 196, 196, 0.1)',
    borderRadius: '0.5rem',
    display: 'inline-block'
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer'
  },
  checkbox: {
    width: 'auto',
    cursor: 'pointer'
  },
  emptyState: {
    color: '#666',
    textAlign: 'center',
    padding: '2rem'
  },
  taskImage: {
    width: window.innerWidth < 768 ? '100%' : '120px',
    height: window.innerWidth < 768 ? 'auto' : '120px',
    maxHeight: window.innerWidth < 768 ? '200px' : '120px',
    objectFit: 'cover',
    borderRadius: window.innerWidth < 768 ? '1rem' : '0.75rem',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  editButton: {
    padding: window.innerWidth < 768 ? '0.6rem 1.25rem' : '0.5rem 1rem',
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    color: 'white',
    border: 'none',
    borderRadius: window.innerWidth < 768 ? '1.25rem' : '0.5rem',
    cursor: 'pointer',
    fontSize: window.innerWidth < 768 ? '0.9rem' : '0.85rem',
    fontWeight: 600,
    transition: 'all 0.2s',
    minHeight: window.innerWidth < 768 ? '44px' : 'auto',
    boxShadow: '0 2px 4px rgba(89, 196, 196, 0.2)',
    whiteSpace: 'nowrap'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    padding: '2rem',
    borderRadius: '12px',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 600,
    fontSize: '0.9rem'
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.95rem',
    boxSizing: 'border-box'
  },
  button: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 600,
    transition: 'all 0.2s'
  },
  buttonPrimary: {
    background: '#59c4c4',
    color: 'white'
  },
  buttonSecondary: {
    background: '#e5e7eb',
    color: '#374151'
  }
};

export default TaskLibrary;
